import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { CartItem } from "./CatalogPage";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

type Line = CartItem & { touched?: boolean };
type Shortage = { material: string; requiredG: number; availableG: number; shortG: number; components: string[] };

const CUR: Record<string, string> = { eg: "EGP", sa: "SAR" };
const STATUS_AR: Record<string, string> = {
  draft: "مسودة", confirmed: "مؤكّد", "in-production": "في الإنتاج",
  delivered: "اتسلّم", cancelled: "ملغي", returned: "مرتجع",
};
const NEXT: Record<string, { to: string; label: string; danger?: boolean }[]> = {
  draft: [{ to: "confirmed", label: "✔ تأكيد الطلب" }, { to: "cancelled", label: "إلغاء", danger: true }],
  confirmed: [{ to: "in-production", label: "🏭 ابدأ الإنتاج (خصم الخامات)" }, { to: "cancelled", label: "إلغاء", danger: true }],
  "in-production": [{ to: "delivered", label: "📦 تم التسليم" }, { to: "cancelled", label: "↩︎ إلغاء + استرجاع الخامات", danger: true }],
  delivered: [{ to: "returned", label: "↩︎ مرتجع — استرجاع الخامات", danger: true }],
};

async function api(path: string, init: RequestInit = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" }, ...init,
  });
  const j = await res.json().catch(() => null);
  if (!res.ok) throw Object.assign(new Error(typeof j?.detail === "string" ? j.detail : `HTTP ${res.status}`), { detail: j?.detail, status: res.status });
  return j;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

function loadCart(): Line[] {
  try { return JSON.parse(sessionStorage.getItem("order_cart") || "[]"); } catch { return []; }
}

export default function OrdersPage() {
  const [market, setMarket] = useState<"eg" | "sa">("eg");
  const [cart, setCart] = useState<Line[]>(loadCart);
  const [catalog, setCatalog] = useState<Record<string, any>>({});
  const [costs, setCosts] = useState<Record<string, number | null>>({});
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [orders, setOrders] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [shortage, setShortage] = useState<{ orderId: string; shortages: Shortage[]; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const persist = (next: Line[]) => { setCart(next); sessionStorage.setItem("order_cart", JSON.stringify(next)); };

  const loadOrders = useCallback(() => {
    api(`/orders?q=${encodeURIComponent(q)}&status=${statusFilter}`).then(setOrders).catch((e) => toast.error(e.message));
  }, [q, statusFilter]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  useEffect(() => {
    api("/catalog?includeArchived=1").then((rows: any[]) => {
      const map: Record<string, any> = {};
      rows.forEach((r) => { map[r.id] = r; });
      setCatalog(map);
    }).catch(() => null);
  }, []);

  // default prices from retail_by_market for untouched lines
  useEffect(() => {
    if (!Object.keys(catalog).length || !cart.length) return;
    let changed = false;
    const next = cart.map((l) => {
      if (l.touched) return l;
      const b = catalog[l.productId];
      const retail = b?.retail_by_market?.[market] ?? b?.retail_price ?? null;
      if (retail != null && l.unitPrice !== retail) { changed = true; return { ...l, unitPrice: retail }; }
      return l;
    });
    if (changed) persist(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, market, cart.length]);

  // per-line COGS
  useEffect(() => {
    cart.forEach((l) => {
      const key = `${l.productId}-${l.sizeMl}-${market}`;
      if (costs[key] !== undefined) return;
      api(`/catalog/${l.productId}/cost?marketId=${market}&sizeMl=${l.sizeMl}`)
        .then((c) => setCosts((p) => ({ ...p, [key]: c.costPerBottle })))
        .catch(() => setCosts((p) => ({ ...p, [key]: null })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, market]);

  const updateLine = (i: number, patch: Partial<Line>) => {
    const next = [...cart];
    next[i] = { ...next[i], ...patch };
    persist(next);
  };

  const cartTotal = useMemo(() => r2(cart.reduce((s, l) => s + l.qty * l.unitPrice, 0)), [cart]);

  const createOrder = async () => {
    if (!customer.name.trim()) { toast.error("اسم العميل مطلوب"); return; }
    if (!cart.length) { toast.error("الطلب فاضي — ضيف منتجات من الكتالوج"); return; }
    setBusy(true);
    try {
      const o = await api("/orders", {
        method: "POST",
        body: JSON.stringify({ customer, marketId: market, items: cart.map(({ touched, ...l }) => l) }),
      });
      toast.success(`اتعمل الطلب ${o.orderNo}`);
      persist([]); setCustomer({ name: "", phone: "" });
      loadOrders();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const transition = async (orderId: string, to: string, override = false) => {
    setBusy(true);
    try {
      const o = await api(`/orders/${orderId}/status`, { method: "PATCH", body: JSON.stringify({ status: to, override }) });
      setShortage(null);
      if (to === "in-production") toast.success(`${o.orderNo} في الإنتاج — اتخصمت الخامات ✓${o.overrideUsed ? " (Override)" : ""}`);
      else if (o.restocked?.length) toast.success(`${o.orderNo} — اترجّعت ${o.restocked.length} خامة للمخزون ✓`);
      else toast.success(`${o.orderNo} → ${STATUS_AR[to] || to}`);
      loadOrders();
    } catch (e: any) {
      if (e.status === 409 && e.detail?.error === "insufficient_stock") {
        setShortage({ orderId, shortages: e.detail.shortages, message: e.detail.message });
      } else toast.error(typeof e.detail === "string" ? e.detail : e.message);
    } finally { setBusy(false); }
  };

  const cur = CUR[market];

  return (
    <div className="space-y-4" data-testid="orders-page">
      {/* ── New order (cart) ── */}
      <div className="bl-card" data-testid="order-cart">
        <div className="bl-eyebrow mb-2">🧾 طلب جديد</div>
        <div className="flex gap-2 items-center flex-wrap mb-3">
          <button className={`bl-tab ${market === "eg" ? "active" : ""}`} onClick={() => setMarket("eg")} data-testid="orders-market-eg">مصر · EGP</button>
          <button className={`bl-tab ${market === "sa" ? "active" : ""}`} onClick={() => setMarket("sa")} data-testid="orders-market-sa">السعودية · SAR</button>
          <input className="bl-input" style={{ maxWidth: 180 }} placeholder="اسم العميل" value={customer.name}
            onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="order-customer-name" />
          <input className="bl-input" style={{ maxWidth: 150 }} placeholder="التليفون" value={customer.phone}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="order-customer-phone" />
        </div>
        {!cart.length ? (
          <div className="bl-sub py-4 text-center" data-testid="order-cart-empty">مفيش منتجات — روح للكتالوج واضغط «أضف للطلب»</div>
        ) : (
          <table className="bl-table" data-testid="order-cart-table">
            <thead><tr><th>المنتج</th><th>الكمية</th><th>الحجم ml</th><th>السعر/قطعة</th><th>الإجمالي</th><th>التكلفة</th><th>هامش السطر</th><th /></tr></thead>
            <tbody>
              {cart.map((l, i) => {
                const cogs = costs[`${l.productId}-${l.sizeMl}-${market}`];
                const lineMargin = cogs != null && l.unitPrice > 0 ? r2(((l.unitPrice - cogs) / l.unitPrice) * 100) : null;
                return (
                  <tr key={l.productId} data-testid={`order-line-${l.code}`}>
                    <td><span className="bl-en">{l.name}</span> <span className="bl-sub bl-en">{l.code}</span></td>
                    <td><input type="number" min={1} className="bl-input" style={{ width: 64 }} value={l.qty}
                      onChange={(e) => updateLine(i, { qty: Math.max(1, Number(e.target.value) || 1) })} data-testid={`order-line-qty-${l.code}`} /></td>
                    <td><input type="number" min={1} className="bl-input" style={{ width: 70 }} value={l.sizeMl}
                      onChange={(e) => updateLine(i, { sizeMl: Math.max(1, Number(e.target.value) || 50) })} data-testid={`order-line-size-${l.code}`} /></td>
                    <td><input type="number" min={0} className="bl-input" style={{ width: 90 }} value={l.unitPrice}
                      onChange={(e) => updateLine(i, { unitPrice: Number(e.target.value) || 0, touched: true })} data-testid={`order-line-price-${l.code}`} /></td>
                    <td className="bl-en">{r2(l.qty * l.unitPrice)} {cur}</td>
                    <td className="bl-en" data-testid={`order-line-cogs-${l.code}`}>{cogs != null ? `${cogs} ${cur}` : "—"}</td>
                    <td className="bl-en" style={{ color: lineMargin != null && lineMargin < 40 ? "var(--bl-danger)" : "var(--bl-ok)", fontWeight: 700 }}
                      data-testid={`order-line-margin-${l.code}`}>{lineMargin != null ? `${lineMargin}%` : "—"}</td>
                    <td><button className="bl-btn ghost" style={{ padding: "4px 10px" }} onClick={() => persist(cart.filter((_, j) => j !== i))}
                      data-testid={`order-line-remove-${l.code}`}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex gap-3 items-center mt-3">
          <span className="bl-pill" data-testid="order-cart-total">الإجمالي: {cartTotal} {cur}</span>
          <button className="bl-btn primary" disabled={busy || !cart.length} onClick={createOrder} data-testid="order-create-btn">إنشاء الطلب</button>
        </div>
        <p className="bl-sub mt-2" style={{ fontSize: 10 }}>السعر بيتملى تلقائي من سعر البيع لكل سوق (لوحة الهامش) — وتقدر تعدّله يدوي.</p>
      </div>

      {/* ── Shortage modal ── */}
      {shortage && (
        <div className="bl-card" style={{ borderColor: "var(--bl-danger)" }} data-testid="shortage-warning">
          <div className="bl-eyebrow mb-2" style={{ color: "var(--bl-danger)" }}>⚠️ مخزون غير كافي — الإنتاج متوقف</div>
          <p className="bl-sub">{shortage.message}</p>
          <table className="bl-table mt-2" data-testid="shortage-table">
            <thead><tr><th>الخامة</th><th>المطلوب g</th><th>المتاح g</th><th>العجز g</th></tr></thead>
            <tbody>
              {shortage.shortages.map((s) => (
                <tr key={s.material} data-testid={`shortage-row-${s.material}`}>
                  <td className="bl-en">{s.material}</td>
                  <td className="bl-en">{s.requiredG}</td>
                  <td className="bl-en" style={{ color: "var(--bl-danger)" }}>{s.availableG}</td>
                  <td className="bl-en" style={{ color: "var(--bl-danger)", fontWeight: 800 }}>{s.shortG}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2 mt-3">
            <button className="bl-btn ghost" onClick={() => setShortage(null)} data-testid="shortage-cancel-btn">إلغاء — هكمّل المخزون الأول</button>
            <button className="bl-btn danger" disabled={busy} onClick={() => transition(shortage.orderId, "in-production", true)}
              data-testid="shortage-override-btn">تنفيذ رغم النقص (Override صريح)</button>
          </div>
        </div>
      )}

      {/* ── Orders list ── */}
      <div className="bl-card">
        <div className="bl-eyebrow mb-2">📋 الطلبات</div>
        <div className="flex gap-2 items-center flex-wrap mb-3">
          <input className="bl-input" style={{ maxWidth: 240 }} placeholder="ابحث بالكود ORV-O-… / العميل / المنتج"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="orders-search-input" />
          {["", "draft", "confirmed", "in-production", "delivered", "cancelled", "returned"].map((s) => (
            <button key={s} className={`bl-tab ${statusFilter === s ? "active" : ""}`} onClick={() => setStatusFilter(s)}
              data-testid={`orders-filter-${s || "all"}`}>{s ? STATUS_AR[s] : "الكل"}</button>
          ))}
        </div>
        {!orders.length && <div className="bl-sub py-4 text-center" data-testid="orders-empty">مفيش طلبات لسه</div>}
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bl-card" style={{ background: "rgba(255,255,255,0.02)" }} data-testid={`order-card-${o.orderNo}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="bl-en font-extrabold" data-testid={`order-no-${o.orderNo}`}>{o.orderNo}</span>
                <span className={`bl-pill ${o.status === "cancelled" || o.status === "returned" ? "danger" : ""}`}
                  data-testid={`order-status-${o.orderNo}`}>{STATUS_AR[o.status] || o.status}</span>
                <span className="bl-pill">{o.marketId === "sa" ? "السعودية · SAR" : "مصر · EGP"}</span>
                {o.overrideUsed && <span className="bl-pill danger">Override</span>}
                <span className="bl-sub mr-auto">{o.customer?.name} {o.customer?.phone ? `· ${o.customer.phone}` : ""}</span>
              </div>
              <div className="bl-sub mt-1">
                {(o.items || []).map((i: any) => `${i.name} ×${i.qty} (${i.sizeMl}ml)`).join(" · ")}
              </div>
              <div className="flex gap-3 mt-2 flex-wrap items-center">
                <span className="bl-pill">الإجمالي: {o.total} {CUR[o.marketId] || "EGP"}</span>
                {o.cogs != null && <span className="bl-pill" data-testid={`order-cogs-${o.orderNo}`}>COGS: {o.cogs}</span>}
                {o.margin != null && <span className="bl-pill" style={{ color: o.margin < 40 ? "var(--bl-danger)" : "var(--bl-ok)" }}
                  data-testid={`order-margin-${o.orderNo}`}>الهامش: {o.margin}%</span>}
                {o.cogsReversed && <span className="bl-pill danger" data-testid={`order-cogs-reversed-${o.orderNo}`}>COGS معكوس (كان {o.cogsReversed.cogs ?? "—"})</span>}
              </div>
              {(o.deductions?.length || 0) > 0 && (
                <details className="mt-2" data-testid={`order-deductions-${o.orderNo}`}>
                  <summary className="bl-sub" style={{ cursor: "pointer" }}>خصومات المخزون ({o.deductions.length})</summary>
                  <table className="bl-table mt-1">
                    <thead><tr><th>الخامة</th><th>g</th><th>الرصيد بعد</th></tr></thead>
                    <tbody>{o.deductions.map((d: any) => (
                      <tr key={d.materialId}><td className="bl-en">{d.libName}</td><td className="bl-en">{d.grams}</td>
                        <td className="bl-en">{d.stockAfter ?? "غير متتبّع"}</td></tr>))}
                    </tbody>
                  </table>
                </details>
              )}
              {(o.restocked?.length || 0) > 0 && (
                <div className="bl-sub mt-2" style={{ color: "var(--bl-ok)" }} data-testid={`order-restocked-${o.orderNo}`}>
                  ↩︎ اترجّع للمخزون: {o.restocked.map((r: any) => `${r.libName} +${r.grams}g`).join(" · ")}
                </div>
              )}
              {(o.unmatched?.length || 0) > 0 && (
                <div className="bl-sub mt-1" style={{ color: "var(--bl-danger)" }}>غير مطابق للمكتبة: {o.unmatched.join(" · ")}</div>
              )}
              <div className="flex gap-2 mt-3 flex-wrap">
                {(NEXT[o.status] || []).map((t) => (
                  <button key={t.to} className={`bl-btn ${t.danger ? "danger" : "primary"}`} style={{ padding: "6px 14px" }}
                    disabled={busy} onClick={() => transition(o.id, t.to)}
                    data-testid={`order-action-${t.to}-${o.orderNo}`}>{t.label}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
